import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.jsx';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import Footer from '@/components/ui/Footer';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error: authError } = useAuth();
  const { toast } = useToast();

  // Get return URL from location state
  const from = location.state?.from?.pathname || '/';

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = t('auth.login.errors.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('auth.login.errors.email_invalid');
    }

    if (!password) {
      newErrors.password = t('auth.login.errors.password_required');
    } else if (password.length < 6) {
      newErrors.password = t('auth.login.errors.password_min_length');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      console.log('Attempting login...');
      const response = await login(email, password);
      console.log('Login successful:', response);
      
      toast({
        title: t('auth.login.success.title'),
        description: t('auth.login.success.description', { name: response.user?.full_name || t('auth.login.default_user') }),
        duration: 3000,
      });

      console.log('Redirecting to:', from);
      // Redirect to the page user was trying to access or admin panel
      const redirectTo = from && from !== '/' ? from : '/panel/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = t('auth.login.errors.general');
      
      if (error.message.includes('Invalid credentials')) {
        errorMessage = t('auth.login.errors.invalid_credentials');
      } else if (error.message.includes('User not found')) {
        errorMessage = t('auth.login.errors.user_not_found');
      } else if (error.message.includes('Network')) {
        errorMessage = t('auth.login.errors.network_error');
      }
      
      toast({
        title: t('auth.login.errors.title'),
        description: errorMessage,
        variant: "destructive",
        duration: 5000, // Ошибки показываем дольше
      });
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              BookForge2
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('app.subtitle')}
            </p>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('auth.login.title')}</CardTitle>
              <CardDescription>
                {t('auth.login.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {authError && (
                  <Alert variant="destructive">
                    <AlertDescription>{authError}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="your@email.com"
                    required
                    disabled={isLoading}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.login.password')}</Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t('auth.login.password_placeholder')}
                    required
                    disabled={isLoading}
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  {errors.password && (
                    <p className="text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="login-button"
                  className="w-full" 
                  disabled={isLoading}
                >
                  {isLoading ? t('auth.login.logging_in') : t('auth.login.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Footer */}
      <Footer showText={false} />
    </div>
  );
}
